import Loading from 'core/components/Loading';
import useProduct from 'hooks/swr/useProduct';
import { SWR_PRODUCTS_ENDPOINT } from 'hooks/swr/useProducts';
import { isArray, isNil } from 'lodash';
import { MeasureEnum } from 'models/enums/measureEnum';
import { ProductTypeEnum } from 'models/enums/productTypeEnum';
import { forwardRef, useContext, useImperativeHandle } from 'react';
import * as productService from 'services/productService';
import StoreContext from 'stores/context/StoreContext';
import { mutate } from 'swr';
import ProductForm from './ProductForm';
import type { ProductDetail } from 'models/product/productDetail';
import type { ForwardRefRenderFunction } from 'react';

const ProductEdit: ForwardRefRenderFunction<ProductEditRef, ProductEditProps> = (
  { productId, groupId, onSave },
  ref
) => {
  const { data: product, isLoading } = useProduct(productId ?? 0, !isNil(productId));
  const { notifyStore } = useContext(StoreContext);

  let currentProduct: ProductDetail | null = null;

  useImperativeHandle(ref, () => ({
    save: () => {
      if (isNil(currentProduct)) {
        showInvalidProductNotify();
        return;
      }

      !isNil(onSave) && onSave(true, false);

      if (isNil(productId)) {
        productService
          .createProductAsync({
            name: currentProduct?.name ?? '',
            price: currentProduct?.price ?? 0,
            measure: currentProduct?.measure ?? MeasureEnum.Piece,
            type: currentProduct?.type ?? ProductTypeEnum.Simple,
            tagIds: currentProduct?.tags?.map((t) => t.id) ?? [],
            groupId,
            components:
              currentProduct?.components?.map((c) => ({
                componentProductId: c.componentProductId,
                count: c.count,
                notPrintInDocuments: c.notPrintInDocuments,
              })) ?? [],
          })
          .then(async () => {
            !isNil(onSave) && onSave(false, false);

            return await mutate(
              (key) => isArray(key) && key[0] === SWR_PRODUCTS_ENDPOINT,
              undefined
            );
          })
          .catch(() => {
            !isNil(onSave) && onSave(false, true);
            return console.log.bind(console);
          });
      } else {
        productService
          .saveProductAsync(currentProduct.id, {
            name: currentProduct?.name ?? '',
            price: currentProduct?.price ?? 0,
            measure: currentProduct?.measure ?? MeasureEnum.Piece,
            type: currentProduct?.type ?? ProductTypeEnum.Simple,
            tagIds: currentProduct?.tags?.map((t) => t.id) ?? [],
            groupId,
            components:
              currentProduct?.components?.map((p) => ({
                componentProductId: p.componentProductId,
                count: p.count,
                notPrintInDocuments: p.notPrintInDocuments,
              })) ?? [],
          })
          .then(async () => {
            !isNil(onSave) && onSave(false, false);

            return await mutate(
              (key) => isArray(key) && key[0] === SWR_PRODUCTS_ENDPOINT,
              undefined
            );
          })
          .catch(() => {
            !isNil(onSave) && onSave(false, true);
            return console.log.bind(console);
          });
      }
    },
  }));

  const onSubmitInvalid: VoidFunction = () => {
    currentProduct = null;
  };

  const onSubmit = (product: ProductDetail): void => {
    currentProduct = product;
  };

  const showInvalidProductNotify: VoidFunction = () => {
    notifyStore.showNotify(
      'Не удалось сохранить товар. Введенные данные не корректны',
      'error'
    );
  };

  if (isLoading) return <Loading />;

  return (
    <ProductForm
      product={product}
      onSubmitValid={onSubmit}
      onSubmitInvalid={onSubmitInvalid}
    />
  );
};

export interface ProductEditProps {
  productId?: number;
  groupId?: number;
  onSave?: (loading: boolean, error: boolean) => void;
}

export interface ProductEditRef {
  save: () => void;
}

export default forwardRef(ProductEdit);
